import { useId, useMemo, useState, type KeyboardEvent } from 'react';
import { api } from '../../shared/api/client';
import type { BibleBook } from '../../shared/api/types';
import { useAsync } from '../../shared/ui/useAsync';

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, '');

/** "1 juan 1 del 1 al 9" → "1 juan 1:1-9" (igual que el servidor). */
export function spokenToNumeric(input: string): string {
  return input
    .trim()
    .replace(/(\d)\s+(?:del|vers[ií]culos?|vv?\.?)\s+(?:el\s+)?(\d)/gi, '$1:$2')
    .replace(/(\d)\s+(?:al|a|hasta(?:\s+el)?)\s+(?:el\s+)?(\d)/gi, '$1-$2');
}

const PATTERN = /^(.+?)\s*(\d{1,3})(?:\s*[:.,]\s*(\d{1,3})(?:\s*[-–]\s*(\d{1,3}))?)?$/;

type Option = { value: string; label: string; complete: boolean };

/** Sugerencias para lo escrito: libros que empiezan así o la cita completa ya entendida. */
export function suggest(books: BibleBook[], text: string): Option[] {
  const q = spokenToNumeric(text);
  if (!q) return [];
  const m = PATTERN.exec(q);
  const head = fold(m ? m[1]! : q);
  // "1 juan" también es "1juan"; si el libro empieza con número, el número es parte del nombre.
  const byPrefix = (key: string) => books.filter((b) => fold(b.name).startsWith(key));
  if (m) {
    const matches = byPrefix(head);
    const book = matches.find((b) => fold(b.name) === head) ?? (matches.length >= 1 ? matches[0] : undefined);
    const chapter = Number(m[2]);
    if (book && chapter >= 1 && chapter <= book.chapters) {
      const start = m[3] ? Number(m[3]) : null;
      const end = m[4] ? Number(m[4]) : null;
      const out: Option[] = [];
      if (start && end && end > start) out.push({ value: `${book.name} ${chapter}:${start}-${end}`, label: `${book.name} ${chapter}:${start}-${end}`, complete: true });
      else if (start) out.push({ value: `${book.name} ${chapter}:${start}`, label: `${book.name} ${chapter}:${start}`, complete: true });
      else out.push({ value: `${book.name} ${chapter}`, label: `${book.name} ${chapter} (capítulo completo)`, complete: true });
      return out.concat(matches.filter((b) => b !== book).slice(0, 4).map((b) => ({ value: `${b.name} ${chapter}`, label: `${b.name} ${chapter}`, complete: chapter <= b.chapters })));
    }
    // Un libro con número sin capítulo todavía ("1 jua" se lee como libro «jua» capítulo 1): sugiere libros.
    const whole = byPrefix(fold(q));
    if (whole.length) return whole.slice(0, 8).map((b) => ({ value: `${b.name} `, label: b.name, complete: false }));
    // Abreviaturas ("jn 3:16", "1 Co 13"): las entiende el servidor.
    return [{ value: q, label: `Buscar «${q}»`, complete: true }];
  }
  return byPrefix(head)
    .slice(0, 8)
    .map((b) => ({ value: `${b.name} `, label: b.name, complete: false }));
}

/**
 * Buscador de pasajes con lista desplegable (combobox): escribe "1 jn 1:1-9" o "1 juan 1 del 1 al 9",
 * elige de la lista y se inserta. Con el teclado: flechas, Enter y Escape.
 */
export function PassagePicker({ onPick, busy = false, label = 'Insertar pasaje', books: given }: { onPick: (ref: string) => void; busy?: boolean; label?: string; books?: BibleBook[] }) {
  const id = useId();
  // Con la lista de libros ya cargada (varios buscadores en una página) no se vuelve a pedir.
  const books = useAsync((signal) => (given ? Promise.resolve(given) : api.get<{ items: BibleBook[] }>('/bible/books', signal).then((r) => r.items)), [given]);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const options = useMemo(() => (books.status === 'ready' ? suggest(books.data, text) : []), [books, text]);

  function choose(o: Option) {
    if (o.complete) {
      onPick(o.value);
      setText('');
      setOpen(false);
    } else {
      setText(o.value);
      setActive(0);
    }
  }

  function key(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = options[active];
      if (o) choose(o);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="field combo">
      <label htmlFor={`${id}-q`}>{label}</label>
      <input
        id={`${id}-q`}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-expanded={open && options.length > 0}
        aria-controls={`${id}-list`}
        aria-activedescendant={open && options[active] ? `${id}-o${active}` : undefined}
        placeholder="Ej. 1 Juan 1:1-9 o «1 juan 1 del 1 al 9»"
        value={text}
        disabled={busy}
        onChange={(e) => {
          setText(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={key}
      />
      {open && options.length > 0 ? (
        <ul id={`${id}-list`} role="listbox" className="combo__list" aria-label="Pasajes sugeridos">
          {options.map((o, i) => (
            <li
              key={o.value + i}
              id={`${id}-o${i}`}
              role="option"
              aria-selected={i === active}
              className={`combo__option${i === active ? ' combo__option--active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(o);
              }}
            >
              {o.complete ? '📖 ' : ''}
              {o.label}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
